import {
  Fragment,
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { Anchor, Button, Group, Stack, Table, Text, Textarea, Title } from '@mantine/core';

import type { BlockModel, DocxModel, InlineSegment, TableCellModel } from '@/lib/ooxml';
import {
  cellTargets,
  isEditableBlock,
  paragraphTarget,
  type DocxEditBinding,
  type EditTarget,
} from './editing';
import { computeListMarkers } from './listMarkers';
import classes from './DocxContent.module.css';

interface DocxBlockListProps {
  model: DocxModel;
  /** image relationship id → object URL (created by the container) */
  imageUrls: Record<string, string>;
  /** Text editing (the workbench's Document view); absent, read-only. */
  edit?: DocxEditBinding;
  /** What a caller adds at a block's end (a paragraph's citation chips). */
  decorate?: (block: BlockModel) => ReactNode;
}

/** Footnote id → display number, in order of first reference. */
function footnoteNumbering(model: DocxModel): Map<string, number> {
  const order = new Map<string, number>();
  for (const block of model.blocks) {
    for (const id of block.footnoteRefs) {
      if (!order.has(id)) {
        order.set(id, order.size + 1);
      }
    }
  }
  return order;
}

function Segments({
  segments,
  footnotes,
  imageUrls,
}: Readonly<{
  segments: InlineSegment[];
  footnotes: Map<string, number>;
  imageUrls: Record<string, string>;
}>) {
  return (
    <>
      {segments.map((seg, i) => {
        const key = `${i}`;
        switch (seg.type) {
          case 'text':
            // Direct run colour is semantic in GTI reports (RAG dots) —
            // preserve it; everything else stays normalized typography.
            return seg.color ? (
              <span key={key} style={{ color: `#${seg.color}` }}>
                {seg.text}
              </span>
            ) : (
              <Fragment key={key}>{seg.text}</Fragment>
            );
          case 'link':
            return (
              <Anchor key={key} href={seg.href} target="_blank" rel="noopener noreferrer" inherit>
                {seg.text}
              </Anchor>
            );
          case 'footnote_ref':
            return (
              <sup key={key} className={classes.footnoteRef}>
                <a href={`#docx-footnote-${seg.id}`}>[{footnotes.get(seg.id) ?? seg.id}]</a>
              </sup>
            );
          case 'image': {
            const url = imageUrls[seg.rId];
            return url ? <img key={key} src={url} alt="" className={classes.inlineImage} /> : null;
          }
          default:
            return null;
        }
      })}
    </>
  );
}

function TocBlock({ model }: Readonly<{ model: DocxModel }>) {
  // Regenerated live from the heading tree (cached page numbers in the
  // document are meaningless in a reflowing view).
  const headings = model.blocks.filter((b) => b.kind === 'heading' && b.text.trim());
  return (
    <nav className={classes.toc} aria-label="Contents">
      <Text fw={700} size="sm" c="dimmed">
        Contents
      </Text>
      {headings.map((h) => (
        <Text
          key={h.pos}
          size="sm"
          className={classes.tocEntry}
          style={{ paddingLeft: `${((h.level ?? 1) - 1) * 16}px` }}
        >
          <a href={`#docx-block-${h.pos}`}>{h.text}</a>
        </Text>
      ))}
    </nav>
  );
}

function anchorProps(block: BlockModel) {
  return {
    id: `docx-block-${block.pos}`,
    'data-pos': block.pos,
    'data-para-id': block.paraId ?? undefined,
    'data-occurrence': block.paraId ? block.occurrence : undefined,
  };
}

interface InlineEditorProps {
  value: string;
  onChange: (text: string) => void;
  onDone: () => void;
  onUndo: () => void;
}

/** How long typing pauses before the edit is handed up. */
export const EDIT_PAUSE_MS = 400;

/**
 * The text of one paragraph, being edited. The text lives here while it's
 * typed, so the document around it doesn't re-render on every keystroke. The
 * edit is handed up at its first change (from then on it waits to be saved,
 * and leaving the page warns), at each pause, and when the editor closes —
 * Done, Enter, Escape, a click elsewhere.
 */
function InlineEditor({ value, onChange, onDone, onUndo }: Readonly<InlineEditorProps>) {
  const [text, setText] = useState(value);
  const committed = useRef(value);
  const latest = useRef(text);
  const started = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  const commit = (next: string) => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (next !== committed.current) {
      committed.current = next;
      onChangeRef.current(next);
    }
  };

  // Closing by any route hands up what was typed since the last pause.
  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
        if (latest.current !== committed.current) {
          onChangeRef.current(latest.current);
        }
      }
    },
    []
  );

  const change = (next: string) => {
    setText(next);
    latest.current = next;
    if (!started.current) {
      started.current = true;
      commit(next);
      return;
    }
    if (timer.current !== null) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => commit(latest.current), EDIT_PAUSE_MS);
  };

  const done = () => {
    commit(latest.current);
    onDone();
  };

  return (
    <Stack gap={4}>
      <Textarea
        aria-label="Edit text"
        autosize
        minRows={1}
        autoFocus
        value={text}
        onChange={(event) => change(event.currentTarget.value)}
        onBlur={() => commit(latest.current)}
        onKeyDown={(event) => {
          // Enter finishes; Shift+Enter is a line break in the paragraph.
          if (event.key === 'Escape' || (event.key === 'Enter' && !event.shiftKey)) {
            event.preventDefault();
            done();
          }
        }}
      />
      <Group gap={4}>
        <Button size="compact-xs" onClick={done}>
          Done
        </Button>
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={() => {
            if (timer.current !== null) {
              clearTimeout(timer.current);
              timer.current = null;
            }
            committed.current = latest.current;
            onUndo();
          }}
        >
          Undo this change
        </Button>
        <Text size="xs" c="dimmed">
          Enter to finish · Shift+Enter for a line break
        </Text>
      </Group>
    </Stack>
  );
}

function cx(...names: (string | false | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

/** Props that make an element open its text for editing. */
function editableProps(edit: DocxEditBinding, target: EditTarget, edited: boolean) {
  return {
    role: 'button',
    tabIndex: 0,
    title: edited ? 'Edited — click to change it again' : 'Click to edit',
    'data-editable': target.key,
    onClick: (event: MouseEvent) => {
      event.stopPropagation();
      edit.onActivate(target);
    },
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        edit.onActivate(target);
      }
    },
  };
}

function editorFor(edit: DocxEditBinding, target: EditTarget) {
  return (
    <InlineEditor
      value={edit.pending.get(target.key)?.text ?? target.expected}
      onChange={(text) => edit.onChange(target, text)}
      onDone={edit.onClose}
      onUndo={() => {
        edit.onChange(target, target.expected);
        edit.onClose();
      }}
    />
  );
}

interface CellParagraphProps {
  target: EditTarget;
  /** The paragraph as it reads; null for an empty cell, to fill. */
  segments: InlineSegment[] | null;
  edit: DocxEditBinding;
  footnotes: Map<string, number>;
  imageUrls: Record<string, string>;
}

/** One paragraph of a table cell, editable — or an empty cell, to fill. */
function CellParagraph({
  target,
  segments,
  edit,
  footnotes,
  imageUrls,
}: Readonly<CellParagraphProps>) {
  if (edit.active === target.key) {
    return editorFor(edit, target);
  }
  const pending = edit.pending.get(target.key);
  return (
    <Text
      size="sm"
      c={!segments && !pending ? 'dimmed' : undefined}
      className={cx(classes.editable, !!pending && classes.edited)}
      {...editableProps(edit, target, !!pending)}
    >
      {pending ? (
        pending.text
      ) : segments ? (
        <Segments segments={segments} footnotes={footnotes} imageUrls={imageUrls} />
      ) : (
        'Add text'
      )}
    </Text>
  );
}

interface CellContentProps {
  block: BlockModel;
  row: number;
  col: number;
  cell: TableCellModel;
  edit?: DocxEditBinding;
  footnotes: Map<string, number>;
  imageUrls: Record<string, string>;
}

/** A cell's paragraphs. Editing, those an edit can address open in place (an
 * empty cell is one paragraph to fill); the rest read as they are. */
function CellContent({
  block,
  row,
  col,
  cell,
  edit,
  footnotes,
  imageUrls,
}: Readonly<CellContentProps>) {
  const targets = edit ? cellTargets(block, row, col, cell) : [];
  const [empty] = targets;
  if (edit && cell.paragraphs.length === 0 && empty) {
    return (
      <CellParagraph
        target={empty}
        segments={null}
        edit={edit}
        footnotes={footnotes}
        imageUrls={imageUrls}
      />
    );
  }
  return (
    <>
      {cell.paragraphs.map((segments, i) => {
        const target = targets[i];
        return edit && target ? (
          <CellParagraph
            key={i}
            target={target}
            segments={segments}
            edit={edit}
            footnotes={footnotes}
            imageUrls={imageUrls}
          />
        ) : (
          <Text key={i} size="sm">
            <Segments segments={segments} footnotes={footnotes} imageUrls={imageUrls} />
          </Text>
        );
      })}
    </>
  );
}

/** The document's blocks. Memoised: `DocxContent` hands it a new binding only
 * when the edits waiting or the paragraph being edited change. */
export const DocxBlockList = memo(
  ({ model, imageUrls, edit, decorate }: Readonly<DocxBlockListProps>) => {
    const markers = useMemo(() => computeListMarkers(model.blocks), [model]);
    const footnotes = useMemo(() => footnoteNumbering(model), [model]);
    const referenced = Array.from(footnotes.keys()).filter((id) => model.footnotes[id]);

    return (
      <div className={classes.content}>
        {model.blocks.map((block) => {
          const common = anchorProps(block);
          const target = edit && isEditableBlock(block) ? paragraphTarget(block) : null;
          if (edit && target && edit.active === target.key) {
            return (
              <div key={block.pos} {...common} className={classes.editingBlock}>
                {editorFor(edit, target)}
              </div>
            );
          }
          const pending = edit && target ? edit.pending.get(target.key) : undefined;
          const interactive = edit && target ? editableProps(edit, target, !!pending) : {};
          const editClass = target ? cx(classes.editable, !!pending && classes.edited) : undefined;
          const segments = (
            <>
              {pending ? (
                pending.text
              ) : (
                <Segments segments={block.segments} footnotes={footnotes} imageUrls={imageUrls} />
              )}
              {decorate?.(block)}
            </>
          );
          switch (block.kind) {
            case 'heading': {
              const order = Math.min(Math.max(block.level ?? 1, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6;
              return (
                <Title
                  key={block.pos}
                  order={order}
                  className={cx(classes.heading, editClass)}
                  {...common}
                  {...interactive}
                >
                  {segments}
                </Title>
              );
            }
            case 'list_item':
              return (
                <div
                  key={block.pos}
                  className={cx(classes.listItem, editClass)}
                  style={{ paddingLeft: `${(block.level ?? 0) * 24 + 8}px` }}
                  {...common}
                  {...interactive}
                >
                  <span className={classes.listMarker}>{markers.get(block.pos)}</span>
                  <Text span inherit>
                    {segments}
                  </Text>
                </div>
              );
            case 'table':
              // Wide tables scroll inside their own container rather than
              // overflowing the content column.
              return (
                <div key={block.pos} className={classes.tableScroll} {...common}>
                  <Table withTableBorder withColumnBorders className={classes.table}>
                    <Table.Tbody>
                      {block.table?.rows.map((row, r) => (
                        // Grid rows are positionally stable for a parsed document.

                        <Table.Tr key={r}>
                          {row.map(
                            (cell, c) =>
                              cell.isMaster && (
                                <Table.Td
                                  key={c}
                                  rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                                  colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                                  data-row={r}
                                  data-col={c}
                                  // The document's own shading: a heading
                                  // row's, a rating's colour.
                                  style={cell.fill ? { backgroundColor: cell.fill } : undefined}
                                >
                                  <CellContent
                                    block={block}
                                    row={r}
                                    col={c}
                                    cell={cell}
                                    edit={edit}
                                    footnotes={footnotes}
                                    imageUrls={imageUrls}
                                  />
                                </Table.Td>
                              )
                          )}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              );
            case 'toc':
              return (
                <div key={block.pos} {...common}>
                  <TocBlock model={model} />
                </div>
              );
            case 'image':
              return (
                <div key={block.pos} className={classes.imageBlock} {...common}>
                  <Segments segments={block.segments} footnotes={footnotes} imageUrls={imageUrls} />
                </div>
              );
            case 'unsupported':
              return (
                <div key={block.pos} className={classes.unsupported} {...common}>
                  <Text size="xs" c="dimmed">
                    Unsupported content ({block.unsupportedTag}) — view the Presentation tab for
                    full fidelity.
                  </Text>
                </div>
              );
            case 'paragraph':
            default: {
              if (!pending && !block.text.trim() && !block.hasImage) {
                return (
                  <div
                    key={block.pos}
                    className={cx(classes.emptyParagraph, editClass)}
                    {...common}
                    {...interactive}
                  />
                );
              }
              return (
                <Text
                  key={block.pos}
                  className={cx(
                    block.inTextbox ? classes.textboxParagraph : classes.paragraph,
                    editClass
                  )}
                  {...common}
                  {...interactive}
                >
                  {segments}
                </Text>
              );
            }
          }
        })}

        {referenced.length > 0 && (
          <div className={classes.footnotes}>
            {referenced.map((id) => (
              <Text key={id} size="xs" id={`docx-footnote-${id}`} className={classes.footnote}>
                <sup>[{footnotes.get(id)}]</sup> {model.footnotes[id]}
              </Text>
            ))}
          </div>
        )}
      </div>
    );
  }
);
